import {ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';

@Component({
  selector: 'mx-phone-form-field',
  template: `
    <mx-input-form-field [fieldName]="fieldName" [overrideLabel]="overrideLabel" [formGroup]="formGroup" [includeClearButton]="includeClearButton"
                         (inputFocused)="onInputFocused()"/>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(PhoneFormFieldComponent)],
  imports: [InputFormFieldComponent]
})
export class PhoneFormFieldComponent implements OnInit, ControlRef {

  @Input() public fieldName: string;
  @Input() public overrideLabel: string;
  @Input() public formGroup: ExtendedFormGroup;
  @Input() public includeClearButton = true;

  @Output() public readonly inputFocused: EventEmitter<never> = new EventEmitter<never>();

  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.control = getExtendedControlFrom(this.formGroup, this.fieldName);

    // For MVP release, manually set the initial value to only the last 10 digits of the phone number.  This is admittedly sloppy and assumes this is
    // a US or Canadian 10-digit phone number for now.  But it's a quick fix to ignore any +1 or other international codes from data migration
    this.updateFormattingFor(this.control.value?.replace(/\D/g, '').slice(-10));

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.updateFormattingFor(value));
  }

  public onInputFocused(): void {
    this.inputFocused.emit();
  }

  private updateFormattingFor(value: string): void {
    const digitsOnlyValue: string = value?.replace(/\D/g, '') ?? '';

    // The valueChanges event fires before the values on the form object update, allowing us to get the previous value.
    // There doesn't appear to be a good way to do nesting for this case, we don't want the control.value
    const [parentName, childName] = this.fieldName.split('.');
    const formValue = childName
      ? this.formGroup.value[parentName][childName]
      : this.formGroup.value[parentName];
    const previousDigitsOnlyValue = formValue?.replace(/\D/g, '') ?? '';

    // This allows the user to backspace without automatically re-adding characters.
    if (digitsOnlyValue.length < 10 && digitsOnlyValue === previousDigitsOnlyValue) return;

    // Format as much as the user has entered to (###) ###-####.
    const formattedValue: string = [
      digitsOnlyValue.length > 0 ? `(${digitsOnlyValue.slice(0, 3)}` : '',
      digitsOnlyValue.length > 2 ? `) ${digitsOnlyValue.slice(3, 6)}` : '',
      digitsOnlyValue.length > 5 ? `-${digitsOnlyValue.slice(6, 10)}` : ''
    ].join('');

    this.control.setValue(formattedValue, {emitEvent: false});
    this.control.markAsDirty();
  }
}
